/**
 * Utilitários de Geolocalização
 */
import storage from "@services/storage";

// Coordenadas das cidades
export const CITIES = {
  uberlandia: {
    name: "Uberlândia",
    slug: "uberlandia",
    state: "MG",
    lat: -18.925397,
    lng: -48.258597,
    address:
      "R. José Paes de Almeida, 84 - Jardim Finotti, Uberlândia - MG, 38408-140",
    theme: "uberlandia",
    unit_id: "2700574d-b911-4264-816b-ee15b226ea4f",
    meta_pixel_id: "709654841304062",
  },
  ribeirao_preto: {
    name: "Ribeirão Preto",
    slug: "ribeirao-preto",
    state: "SP",
    lat: -21.174967,
    lng: -47.83094,
    address: "Av. do Café, 1365 - Vila Amelia, Ribeirão Preto - SP, 14050-230",
    theme: "ribeirao",
    unit_id: "d1168e0f-4090-4ccc-9d8b-2391fcb7353a",
    meta_pixel_id: "1721007521716925",
  },
};


/**
 * Converte graus para radianos
 */
const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Calcula distância entre dois pontos usando fórmula de Haversine
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Raio da Terra em km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Obtém a cidade mais próxima baseado nas coordenadas
 */
export const getNearestCity = (lat, lng) => {
  let nearestCity = CITIES.uberlandia; // Default
  let minDistance = Infinity;

  Object.values(CITIES).forEach((city) => {
    const distance = calculateDistance(lat, lng, city.lat, city.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = city;
    }
  });

  return {
    city: nearestCity,
    distance: minDistance,
  };
};

/**
 * Obtém a localização atual do usuário
 */
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocalização não suportada"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let message = "Erro ao obter localização";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = "Permissão de localização negada";
            break;
          case error.POSITION_UNAVAILABLE:
            message = "Localização indisponível";
            break;
          case error.TIMEOUT:
            message = "Tempo esgotado ao obter localização";
            break;
        }

        reject(new Error(message));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutos de cache
      }
    );
  });
};

/**
 * Detecta a cidade automaticamente baseado na localização
 */
export const detectCity = async () => {
  try {
    const position = await getCurrentPosition();
    const { city, distance } = getNearestCity(position.lat, position.lng);

    // Se a distância for maior que 200km, usa a cidade padrão
    if (distance > 200) {
      return {
        city: CITIES.uberlandia,
        detected: false,
        reason: "too_far",
      };
    }

    return {
      city,
      detected: true,
      distance,
    };
  } catch (error) {
    return {
      city: CITIES.uberlandia,
      detected: false,
      reason: error.message,
    };
  }
};

/**
 * Obtém cidade pelo slug
 */
export const getCityBySlug = (slug) => {
  // Find the city object where its slug property matches the input slug
  return Object.values(CITIES).find(city => city.slug === slug);
};

/**
 * Obtém todas as cidades disponíveis
 */
export const getAllCities = () => {
  return Object.values(CITIES);
};

/**
 * Salva a cidade selecionada no storage
 */
export const saveSelectedCity = (citySlug) => {
  storage.setItem("selected_city", citySlug);
};

/**
 * Obtém a cidade salva no storage
 */
export const getSavedCity = () => {
  const saved = storage.getItem("selected_city");
  return saved ? getCityBySlug(saved) : null;
};

export default {
  calculateDistance,
  getNearestCity,
  getCurrentPosition,
  detectCity,
  getCityBySlug,
  getAllCities,
  saveSelectedCity,
  getSavedCity,
};
